import React, { useState, useEffect, useCallback } from 'react';
import APIService from '../services/api';
import { ancre } from '../tutoriel/ancres';

/**
 * SUIVI DES DOCUMENTS — « qu'est-ce qui est parti et n'est jamais revenu »,
 * et aussi ce qui est revenu : signé, refusé, expiré. Un document y est suivi
 * quel que soit son état (demande 20.09 : « plutôt suivi document, car ça
 * peut être déjà signé ou en attente »).
 *
 * LA PORTÉE VIENT DU SERVEUR : chacun voit SES envois, l'administrateur ceux
 * de toute l'organisation, filtrés par expéditeur et triés à sa main. L'écran
 * apprend du serveur ce qu'il peut proposer (`portee_organisation_permise`),
 * il ne le devine pas depuis le rôle.
 *
 * Demande 18.09 : « où il y a aussi le suivi de NDA envoyé, ou en général
 * suivi de document — car il y aura des CDI etc. envoyés, et donc peut-être
 * en attente de signature ».
 *
 * L'ERP envoyait déjà des NDA, des contrats de mission, des contrats de
 * travail et des CRA à valider. Chaque envoi était rangé, mais ne se relisait
 * que fiche par fiche : personne n'a jamais vu la seule liste qui compte.
 *
 * TOUT VIENT DU SERVEUR : le type, l'état, l'ancienneté, le retard, et la
 * phrase « ce qu'il faut faire ». L'écran ne recalcule aucun seuil — un délai
 * de relance recopié ici diverge du jour où le serveur le change.
 *
 * PLAN DE DESIGN (écran nouveau) — l'unité n'est pas « un document », c'est
 * UNE ATTENTE. D'où une colonne de durées en mono à gauche, alignée, plutôt
 * qu'un tableau Document/Type/Candidat/Date/État : un tableau RANGE, il ne
 * hiérarchise pas, et « ça fait douze jours » y devient une soustraction
 * mentale. Le filet vertical à gauche de chaque ligne grandit avec l'attente
 * et passe au rouge de la marque au-delà du délai de relance : on lit la
 * colonne avant de lire les mots. C'est le seul endroit où l'audace est
 * dépensée — types et états restent des chips calmes, dans les tokens en
 * place (Archivo en titre, Plex Mono pour les durées, bordures 1 px, pas
 * d'ombre, un seul rouge).
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

// « Tout » d'abord (demande 21.09 : « je ne vois pas les NDA que j'ai
// envoyés et qui sont signés ») — l'attente est en haut de toute façon, le
// tri par défaut s'en charge ; cacher le signé faisait chercher un envoi qui
// avait très bien fini. « Signés » est un état précis, servi par le serveur.
const FILTRES = [
  { cle: 'tout', label: 'Tout', q: {} },
  { cle: 'ouvert', label: 'En attente', q: { ouvert: '1' } },
  { cle: 'retard', label: 'Qui traînent', q: { retard: '1' } },
  { cle: 'signes', label: 'Signés', q: { etat: 'completed' } },
];

export default function SuiviDocuments({ showNotification, onOuvrirCandidat, onOuvrirBesoin }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [filtre, setFiltre] = useState('tout');
  const [type, setType] = useState('');
  // Portée, expéditeur et tri : '' = le défaut du serveur (les siens pour un
  // commercial, toute l'organisation pour un administrateur).
  const [portee, setPortee] = useState('');
  const [envoyePar, setEnvoyePar] = useState('');
  const [tri, setTri] = useState('');
  // Période sur la date d'envoi et sens du tri : '' = le défaut du serveur
  // (30 derniers jours, décroissant) — la réponse dit ce qu'elle a servi.
  const [periode, setPeriode] = useState('');
  const [sens, setSens] = useState('');
  const [busy, setBusy] = useState(null); // envelope_id en cours

  const charger = useCallback(async () => {
    setErreur(null);
    const f = FILTRES.find(x => x.cle === filtre) || FILTRES[0];
    const p = new URLSearchParams({ ...f.q, ...(type ? { type } : {}),
      ...(portee ? { portee } : {}), ...(envoyePar ? { envoye_par: envoyePar } : {}), ...(tri ? { tri } : {}),
      ...(periode ? { periode } : {}), ...(sens ? { sens } : {}) });
    try {
      const r = await fetch(`${BASE}/documents/suivi?${p}`, { headers: authH() });
      // Une route absente ne casse pas l'écran : il le DIT, sans rien inventer.
      if (r.status === 404 || r.status === 501) { setErreur('arrive'); setData(null); return; }
      const j = await r.json();
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j);
    } catch (e) { setErreur(e.message); setData(null); }
  }, [filtre, type, portee, envoyePar, tri, periode, sens]);
  useEffect(() => { charger(); }, [charger]);

  const geste = async (doc, quoi) => {
    setBusy(doc.envelope_id);
    try {
      const r = await fetch(`${BASE}/documents/suivi/${encodeURIComponent(doc.envelope_id)}/${quoi}`,
        { method: 'POST', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message, 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const c = data?.compteurs || {};
  const docs = data?.documents || [];
  const organisation = data?.portee === 'organisation';
  // Dès qu'on sort de ses SEULS envois, la ligne doit dire qui l'a envoyée :
  // dans « mes dossiers », la moitié des documents viennent de la main d'un
  // collègue, et c'est justement la réponse à « pourquoi je ne le voyais pas ».
  const avecExpediteur = data?.portee && data.portee !== 'moi';
  const porteeCourante = portee || data?.portee || 'mes_dossiers';
  const porteeServie = (data?.portees || []).find(x => x.cle === data?.portee);
  const periodeServie = (data?.periodes || []).find(x => x.cle === data?.periode);
  const sensServi = sens || data?.sens || 'desc';

  const titre = { fontFamily: "'Archivo',system-ui,sans-serif", fontWeight: 800, letterSpacing: '-0.02em' };
  const chip = (fg, bd) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px',
    fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px',
    border: `1px solid ${bd || 'var(--hair)'}`, color: fg || 'var(--text-2)', whiteSpace: 'nowrap' });

  return (
    <div style={{ padding: '22px 26px', maxWidth: '1060px' }} {...ancre('documents-suivi')}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <h1 style={{ ...titre, fontSize: '26px', margin: 0, color: 'var(--text-strong)' }}>Suivi des documents</h1>
        {data && (
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: 'var(--text-2)' }}>
            {(porteeServie?.libelle || 'vos dossiers').toLowerCase()}{periodeServie ? ` · ${periodeServie.libelle.toLowerCase()}` : ''} · {c.en_attente || 0} en attente
            {c.en_retard ? <span style={{ color: 'var(--danger-fg)', fontWeight: 600 }}> · {c.en_retard} sans réponse</span> : null}
            {c.signes ? ` · ${c.signes} signé${c.signes > 1 ? 's' : ''}` : ''}
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--text-2)', maxWidth: '62ch', lineHeight: 1.55 }}>
        Chaque document envoyé à la signature — NDA, contrats, CRA — et où il en est :
        en attente, signé, refusé, expiré. Ce qui demande un geste est en haut.
      </p>
      {/* Le serveur a décidé la portée et le dit : on ne cache pas un refus. */}
      {data?.portee_refus && (
        <p style={{ margin: '-10px 0 16px', fontSize: '12px', color: 'var(--text-3)' }}>{data.portee_refus}</p>
      )}
      {/* « 3 documents, et 9 ailleurs » est une information autant que
          « 0, et 9 ailleurs » : l'écran ne laisse jamais croire qu'il montre
          tout ce qui est parti. */}
      {data?.hors_portee && docs.length > 0 && (
        <p style={{ margin: '-10px 0 16px', fontSize: '12px', color: 'var(--text-3)' }}>{data.hors_portee.phrase}</p>
      )}

      {/* Filtres : trois vues, pas un panneau de recherche. « Qui traînent »
          est le seul écran qu'on ouvre vraiment le matin. L'administrateur
          reçoit en plus la portée, l'expéditeur et le tri — servis, jamais
          déduits du rôle. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {FILTRES.map(f => (
          <button key={f.cle} onClick={() => setFiltre(f.cle)}
            className={filtre === f.cle ? 'erp-btn erp-btn--primary erp-btn--sm' : 'erp-btn erp-btn--secondary erp-btn--sm'}>
            {f.label}
          </button>
        ))}
        {/* LES PORTÉES SONT SERVIES, pas codées ici : « mes dossiers » veut
            dire quelque chose de précis côté serveur (mes envois + mes besoins
            + mes positionnements), et l'infobulle le dit avec ses mots.
            Celles réservées aux administrateurs ne sont pas proposées aux
            autres — on ne montre pas un bouton qui renverra un refus. */}
        {(data?.portees || []).filter(x => !x.admin_seulement || data?.portee_organisation_permise).length > 1 && (
          <span style={{ display: 'inline-flex', gap: '4px', marginLeft: '6px', padding: '3px', borderRadius: '9px', border: '1px solid var(--hair)' }}>
            {data.portees.filter(x => !x.admin_seulement || data.portee_organisation_permise).map(x => (
              <button key={x.cle} type="button" title={x.explication}
                onClick={() => { setPortee(x.cle); if (x.cle !== 'organisation') setEnvoyePar(''); }}
                className={porteeCourante === x.cle ? 'erp-btn erp-btn--primary erp-btn--sm' : 'erp-btn erp-btn--secondary erp-btn--sm'}
                style={{ border: 'none' }}>
                {x.libelle}
              </button>
            ))}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {organisation && (data?.expediteurs || []).length > 0 && (
          <select value={envoyePar} onChange={e => setEnvoyePar(e.target.value)} className="erp-field"
            style={{ fontSize: '12.5px', padding: '6px 9px', maxWidth: '220px' }} title="Expéditeur">
            <option value="">Tous les expéditeurs</option>
            {data.expediteurs.map(x => <option key={x.id} value={x.id}>{x.nom || `Utilisateur ${x.id}`} ({x.n})</option>)}
          </select>
        )}
        <select value={type} onChange={e => setType(e.target.value)} className="erp-field"
          style={{ fontSize: '12.5px', padding: '6px 9px', maxWidth: '220px' }} title="Type de document">
          <option value="">Tous les types</option>
          {(data?.types || []).map(t => <option key={t.cle} value={t.cle}>{t.libelle}</option>)}
        </select>
        {(data?.periodes || []).length > 0 && (
          <select value={periode || data.periode || 'mois'} onChange={e => setPeriode(e.target.value)} className="erp-field"
            style={{ fontSize: '12.5px', padding: '6px 9px', maxWidth: '170px' }} title="Période, sur la date d'envoi">
            {data.periodes.map(x => <option key={x.cle} value={x.cle}>{x.libelle}</option>)}
          </select>
        )}
        {(data?.tris || []).length > 0 && (
          <>
            <select value={tri || data.tri || 'defaut'} onChange={e => setTri(e.target.value)} className="erp-field"
              style={{ fontSize: '12.5px', padding: '6px 9px', maxWidth: '200px' }} title="Trier">
              {data.tris.filter(t => avecExpediteur || t.cle !== 'expediteur').map(t => <option key={t.cle} value={t.cle}>{t.libelle}</option>)}
            </select>
            {/* Le sens : un bouton, pas une seconde liste — on inverse, on
                n'explique pas. */}
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm"
              onClick={() => setSens(sensServi === 'asc' ? 'desc' : 'asc')}
              title={sensServi === 'asc' ? 'Ordre croissant — cliquer pour inverser' : 'Ordre décroissant — cliquer pour inverser'}
              aria-label="Inverser l'ordre">
              <BI icon={sensServi === 'asc' ? 'sort-up' : 'sort-down'} />
            </button>
          </>
        )}
      </div>

      {erreur === 'arrive' && (
        <div style={{ padding: '16px 18px', borderRadius: '11px', border: '1px solid var(--hair)', fontSize: '13px', color: 'var(--text-2)' }}>
          Le suivi des documents arrive côté serveur. Rien n'est perdu : chaque envoi reste sur la fiche du candidat.
        </div>
      )}
      {erreur && erreur !== 'arrive' && (
        <div style={{ padding: '16px 18px', borderRadius: '11px', border: '1px solid var(--danger-fg)', color: 'var(--danger-fg)', fontSize: '13px' }}>
          {erreur}
        </div>
      )}
      {!erreur && !data && (
        <div style={{ fontSize: '13px', color: 'var(--text-faint)' }}><BI icon="arrow-repeat" /> Chargement…</div>
      )}

      {/* État VIDE : une invitation, pas un constat. */}
      {data && docs.length === 0 && (
        <div style={{ padding: '26px 22px', borderRadius: '12px', border: '1px dashed var(--hair)', textAlign: 'left' }}>
          <div style={{ ...titre, fontSize: '15px', color: 'var(--text-strong)', marginBottom: '5px' }}>
            {filtre === 'tout'
              ? (periodeServie && periodeServie.cle !== 'tout' ? `Aucun document envoyé sur ${periodeServie.libelle.toLowerCase()}` : 'Aucun document suivi pour l’instant')
              : filtre === 'signes' ? 'Rien de signé sur cette période' : 'Rien n’attend de signature'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.55 }}>
            {filtre === 'tout'
              ? (periodeServie && periodeServie.cle !== 'tout'
                ? 'Élargissez la période — « Tout » remonte à l’origine.'
                : (organisation
                  ? "Les documents arrivent ici dès le premier envoi : NDA depuis la fiche d'un candidat, contrat depuis le flux de placement, CRA depuis une ressource — et y restent une fois signés."
                  : "Vos documents arrivent ici dès le premier : NDA depuis la fiche d'un candidat, contrat depuis le flux de placement, CRA depuis une ressource — et y restent une fois signés."))
              : filtre === 'signes'
                ? 'Élargissez la période, ou passez sur « Tout » pour relire l’historique.'
                : 'Tout ce qui est parti est revenu. Passez sur « Tout » pour relire l’historique — signé, refusé ou expiré.'}
          </div>
          {/* « Rien » et « rien À VOUS » ne se lisent pas pareil, et c'est la
              seconde qu'on lisait sans le savoir : un NDA parti de la main
              d'un recruteur sur son propre candidat n'apparaissait nulle part,
              et l'écran laissait conclure qu'aucun n'était parti. Le serveur
              compte ce qu'il ne montre pas ; on le dit. */}
          {data.hors_portee && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-2)' }}>
              {data.hors_portee.phrase}
              {data.portee_organisation_permise && (
                <> <button type="button" onClick={() => setPortee('organisation')}
                  style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--text-strong)', textDecoration: 'underline', cursor: 'pointer' }}>
                  Voir toute l’agence
                </button></>
              )}
            </div>
          )}
        </div>
      )}

      {data && docs.map(d => {
        // La JAUGE : la longueur dit l'attente, la couleur dit le dépassement.
        // Plafonnée à trois semaines — au-delà, le filet plein dit déjà tout.
        const h = Math.max(14, Math.min(Math.round((d.depuis_jours ?? 0) / 21 * 46), 46));
        return (
          <div key={d.envelope_id}
            style={{ display: 'flex', gap: '14px', alignItems: 'stretch', padding: '13px 0', borderTop: '1px solid var(--hair)' }}>
            {/* Colonne des durées — l'élément signature de l'écran. */}
            <div style={{ flex: 'none', width: '58px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span aria-hidden="true" style={{ display: 'block', width: '3px', height: `${h}px`, borderRadius: '2px', marginTop: '3px',
                background: d.en_retard ? 'var(--danger-fg)' : 'var(--hair)' }} />
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '15px', fontWeight: 600, lineHeight: 1.2,
                color: d.en_retard ? 'var(--danger-fg)' : 'var(--text-3)' }}>
                {d.depuis_jours ?? '—'}<span style={{ fontSize: '10px', fontWeight: 400 }}> j</span>
              </span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{d.titre}</span>
                <span style={chip()}>{d.type_libelle}</span>
                <span style={d.attention ? chip('var(--danger-fg)', 'var(--danger-fg)') : chip()}>{d.etat_libelle}</span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '3px' }}>
                {d.candidat && (onOuvrirCandidat && d.candidate_id
                  ? <button onClick={() => onOuvrirCandidat(d.candidate_id)}
                      style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--text-strong)', textDecoration: 'underline', cursor: 'pointer' }}>{d.candidat}</button>
                  : d.candidat)}
                {d.besoin && <> · {onOuvrirBesoin && d.besoin_id
                  ? <button onClick={() => onOuvrirBesoin(d.besoin_id)}
                      style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--text-strong)', textDecoration: 'underline', cursor: 'pointer' }}>{d.besoin}</button>
                  : d.besoin}</>}
                {d.client && ` · ${d.client}`}
                {d.signataire && ` · ${d.signataire}`}
                {/* QUI l'a envoyé — et « envoi automatique » quand personne :
                    jamais « inconnu ». */}
                {avecExpediteur && (
                  <span style={{ color: 'var(--text-3)' }}> · {d.envoye_par_nom ? `envoyé par ${d.envoye_par_nom}` : 'envoi automatique'}</span>
                )}
              </div>
              {d.quoi_faire && (
                <div style={{ fontSize: '12px', marginTop: '4px', color: d.attention ? 'var(--danger-fg)' : 'var(--text-3)' }}>
                  {d.quoi_faire}
                </div>
              )}
            </div>

            <div style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
              {d.ouvert && (
                <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busy === d.envelope_id}
                  onClick={() => geste(d, 'relancer')}
                  title="Renvoyer la demande de signature au signataire">
                  <BI icon={busy === d.envelope_id ? 'arrow-repeat' : 'send'} /> Relancer
                </button>
              )}
              {/* Le webhook peut manquer : sans ce geste, un document signé
                  resterait « en attente » pour toujours — et on relancerait
                  quelqu'un qui a déjà signé. */}
              <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busy === d.envelope_id}
                onClick={() => geste(d, 'resynchroniser')}
                title="Redemander son état au service de signature">
                <BI icon="arrow-clockwise" />
              </button>
              {d.ouvert && (
                <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={busy === d.envelope_id}
                  onClick={() => geste(d, 'annuler')}
                  title="Annuler : le document ne pourra plus être signé">
                  <BI icon="x-lg" />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {data?.partiel && (
        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-faint)' }}>{data.partiel}</div>
      )}
    </div>
  );
}
